import React, { memo, useRef } from 'react';

import { styled } from '@compiled/react';
import Markdown from 'markdown-to-jsx';

import { useResizingHeight } from '@atlaskit/motion';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Stack, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { ConvoAIResponseState, useConvoAIStreaming } from '@atlassian/ai-agent-streaming';
import { AIBorder, AIStreamLoading, AITelepointerWrapper } from '@atlassian/ai-components';
import type {
	AiFeatureRequestEnvelopeConfluenceQuickSummaryRequest,
	QuickSummaryErrorResponseMessage,
	QuickSummaryFinalResponseMessage,
	QuickSummaryFollowUpQueriesResponseMessage,
} from '@atlassian/convo-ai-api';

import { useAISummaryRequestConfig } from '../../../hooks/useAISummaryRequestConfig';
import { AIStreamError } from '../streaming-error';
import { StreamingSummaryFooter } from '../streaming-summary-footer';

const styles = xcss({ width: '500px' });
const MAX_HEIGHT_SUMMARY = 358;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const AISummaryBody = styled.div({
	font: token('font.body.large'),
	maxHeight: `${MAX_HEIGHT_SUMMARY}px`,
	overflowY: 'auto',
	overflowWrap: 'break-word',
	whiteSpace: 'normal',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&::-webkit-scrollbar': {
		width: token('space.100', '8px'),
		background: 'transparent',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&::-webkit-scrollbar-thumb': {
		borderRadius: token('space.100', '8px'),
		backgroundColor: token('color.background.neutral'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.hovered'),
		},
	},
});

export type AIStreamingSummaryContentPros = {
	ari: string;
};

export const AIStreamingSummaryContent = memo(({ ari }: AIStreamingSummaryContentPros) => {
	const [request, config] = useAISummaryRequestConfig(ari);
	const summaryRef = useRef<HTMLDivElement | null>(null);

	const scrollToBottom = () => {
		const summaryBody = summaryRef?.current;
		if (summaryBody) {
			summaryBody.scrollTop = summaryBody.scrollHeight;
		}
	};

	const [{ content, responseState, error }] = useConvoAIStreaming<
		AiFeatureRequestEnvelopeConfluenceQuickSummaryRequest,
		QuickSummaryFinalResponseMessage,
		QuickSummaryErrorResponseMessage,
		QuickSummaryFollowUpQueriesResponseMessage
	>(request, config, scrollToBottom);

	const renderPopupContent = () => {
		switch (responseState) {
			case ConvoAIResponseState.Initialization:
				return <AIStreamLoading />;
			case ConvoAIResponseState.Error:
				return <AIStreamError error={error} />;
			case ConvoAIResponseState.AnswerPart:
			case ConvoAIResponseState.FinalResponse:
				return (
					<Box padding="space.250">
						<Stack space="space.250">
							<AISummaryBody ref={summaryRef} data-testid="ai-summary-body-content">
								<AITelepointerWrapper
									hideTelepointer={responseState === ConvoAIResponseState.FinalResponse}
								>
									<Markdown>{content}</Markdown>
								</AITelepointerWrapper>
							</AISummaryBody>
							{responseState === ConvoAIResponseState.FinalResponse && <StreamingSummaryFooter />}
						</Stack>
					</Box>
				);
		}
	};

	return (
		<Box xcss={styles}>
			<AIBorder loading={responseState === ConvoAIResponseState.Initialization}>
				<div {...useResizingHeight()}>{renderPopupContent()}</div>
			</AIBorder>
		</Box>
	);
});
